import { useState, useEffect } from "react";
import Clarity from "@microsoft/clarity";

interface ICookieConsentState {
  hasConsent: boolean | null; // null = not decided, true = accepted, false = declined
  showBanner: boolean;
}

export const useCookieConsent = () => {
  const [state, setState] = useState<ICookieConsentState>({
    hasConsent: null,
    showBanner: false,
  });

  useEffect(() => {
    try {
      // Check if user has already made a choice
      const savedConsent = localStorage.getItem("cookieConsent");

      if (savedConsent) {
        setState({
          hasConsent: savedConsent === "accepted",
          showBanner: false,
        });
      } else {
        // Show banner after a short delay
        const timer = setTimeout(() => {
          setState((prev) => ({
            ...prev,
            showBanner: true,
          }));
        }, 1000);

        return () => clearTimeout(timer);
      }
    } catch {
      // SecurityError can occur in private browsing mode on mobile Firefox
      // when localStorage is blocked - show banner as fallback
      const timer = setTimeout(() => {
        setState((prev) => ({
          ...prev,
          showBanner: true,
        }));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    try {
      localStorage.setItem("cookieConsent", "accepted");
    } catch {
      // SecurityError in private browsing mode - continue without persisting
    }
    setState({
      hasConsent: true,
      showBanner: false,
    });

    // Initialize tracking services
    initializeTracking();
  };

  const declineCookies = () => {
    try {
      localStorage.setItem("cookieConsent", "declined");
    } catch {
      // SecurityError in private browsing mode - continue without persisting
    }
    setState({
      hasConsent: false,
      showBanner: false,
    });

    // Disable tracking services
    disableTracking();
  };

  const initializeTracking = () => {
    // Initialize Microsoft Clarity
    if (!import.meta.env.VITE_ENVIRONMENT) {
      Clarity.init("q8lud1m0e5");
    }

    // Google Analytics (if used)
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
      });
    }

    // Any other tracking services
  };

  const disableTracking = () => {
    // Disable Microsoft Clarity
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("consent", false);
    }

    // Disable Google Analytics
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
      });
    }

  };

  return {
    hasConsent: state.hasConsent,
    showBanner: state.showBanner,
    acceptCookies,
    declineCookies,
  };
};